import { ExportFormat } from '@ecosystem/newron-client-lib';
import { CreateImagePowerPointParams, ExcelExportParams, ExcelRowArgs, ExportChartArgs } from '../Model';
import { ExportContext, Option, PageName } from '@utils/pageConfig/Types';
import { getContext, getFilename } from '../ExportUtils';
import { createImage, createPowerPoint } from '../ExportChart';
import { ExportTrackingPageView } from '../export-tracking/ExportTracking';
import { AvailableTemplate, DataCell, ExcelExportDto, TableColumn } from '@ecosystem/export-api';
import { saveFile } from '../SaveFile';
import { getExportColumn, getExportRow } from './ExportBubbleChartExcel';
import { constructCurrencyLabel, formatLabelIntoRevenuePriceTitle } from '@utils/currencyUtils/CurrencyUtils';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';

const createExcel = async ({
  filename,
  exportFn,
  appliedCountry,
  appliedPg,
  data,
  factRevenue,
  excelRowArgs,
  title,
  volume = DEFAULT_VOLUME,
  pageName,
  pagesConfig,
  loadedProductGroups,
}: ExcelExportParams) => {
  const dataColumns: TableColumn[] = getExportColumn(factRevenue.currencySymbol, data);
  const dataRows: DataCell[][] = getExportRow(data, factRevenue, volume);

  const exportContext: ExportContext = {
    appliedCountry: appliedCountry,
    appliedPg: appliedPg,
    timePeriod: excelRowArgs.timePeriod,
    periodType: excelRowArgs.periodType,
    comparisonTimePeriod: excelRowArgs.comparisonTimePeriod,
    showPerformanceWithin: excelRowArgs.showPerformanceWithin,
    appliedFiltersText: excelRowArgs.appliedFiltersText,
    pageName: pageName,
    pagesConfig: pagesConfig,
    loadedProductGroups: loadedProductGroups,
  };
  const getColumnWidths = (): { columnNumber: number; width: number }[] => {
    return [
      { columnNumber: 1, width: 25 },
      { columnNumber: 2, width: 35 },
      ...Array(dataColumns.length)
        .fill(3)
        .map((_, i) => ({ columnNumber: i + 3, width: 25 })),
    ];
  };
  const exportData: ExcelExportDto = {
    template: AvailableTemplate.Excel,
    name: filename,
    worksheets: [
      {
        worksheetName: title,
        context: getContext(exportContext),
        data: {
          dataRows,
          dataColumns,
          columnWidths: getColumnWidths(),
          currencySymbol: factRevenue.currencySymbol,
        },
      },
    ],
  };

  const exportFile = await exportFn(exportData);
  saveFile(exportFile);
};

export async function exportBubbleChart(args: ExportChartArgs): Promise<void> {
  const {
    exportFn,
    exportFormats,
    invokeExport,
    title,
    appliedCountry,
    appliedPg,
    data,
    factRevenue,
    timePeriod,
    comparisonTimePeriod,
    periodType,
    showPerformanceWithin,
    appliedFiltersText,
    selectedKpi,
    segmentBy,
    volume,
    pagesConfig,
    loadedProductGroups,
  } = args;
  const rowArgs: ExcelRowArgs = {
    timePeriod,
    comparisonTimePeriod,
    periodType,
    showPerformanceWithin,
    appliedFiltersText,
    selectedKpi,
  };

  const currencyLabel = constructCurrencyLabel(factRevenue);
  const formattedSegmentBy = formatLabelIntoRevenuePriceTitle(segmentBy, currencyLabel);
  const formattedKpiLabel: Option = {
    ...selectedKpi,
    label: formatLabelIntoRevenuePriceTitle(selectedKpi.label, currencyLabel),
  };

  const filename = getFilename({
    title,
    appliedCountry,
    appliedPg,
    timeframe: timePeriod,
    comparisonTimePeriod,
    exportFormat: ExportFormat.Xlsx,
    selectedKpi: selectedKpi,
    pageName: PageName.OPPORTUNITY_REVIEW,
  });

  if (exportFormats.includes(ExportFormat.Png)) {
    const methodParam: CreateImagePowerPointParams = {
      filename,
      exportFn,
      appliedCountry,
      appliedPg,
      timePeriod,
      comparisonTimePeriod,
      periodType,
      showPerformanceWithin,
      appliedFiltersText,
      invokeExport,
      selectedKpi: formattedKpiLabel,
      segmentBy: formattedSegmentBy,
      title: title,
      subTitle: selectedKpi.label,
      pageName: PageName.OPPORTUNITY_REVIEW,
      pageView: ExportTrackingPageView.BubbleChart,
      pagesConfig,
      loadedProductGroups,
    };
    await createImage(methodParam, title);
  }
  if (exportFormats.includes(ExportFormat.Pptx)) {
    const methodParam: CreateImagePowerPointParams = {
      filename,
      exportFn,
      appliedCountry,
      appliedPg,
      timePeriod,
      comparisonTimePeriod,
      periodType,
      showPerformanceWithin,
      appliedFiltersText,
      invokeExport,
      selectedKpi: selectedKpi,
      segmentBy,
      title: title,
      subTitle: formattedSegmentBy,
      pageName: PageName.OPPORTUNITY_REVIEW,
      pagesConfig,
      loadedProductGroups,
    };
    await createPowerPoint(methodParam);
  }
  if (exportFormats.includes(ExportFormat.Xlsx)) {
    await createExcel({
      filename,
      exportFn,
      appliedCountry,
      appliedPg,
      data,
      factRevenue,
      excelRowArgs: rowArgs,
      title,
      volume,
      pageName: PageName.OPPORTUNITY_REVIEW,
      pagesConfig,
      loadedProductGroups,
    });
  }
}
